import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ContentContainer, VanillaBackground } from "@faire/retailer-visitor-shared/components/GiftGuide/common";
import { EAEAEA_PIXEL } from "@faire/retailer-visitor-shared/consts/EAEAEA_PIXEL";
import { isLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { optimizeImageUrl } from "@faire/web--source/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Button } from "@faire/web--source/slate/Button";
import { Column, Flex } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { Anchor } from "@faire/web--source/ui/LinkAnchor";
import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";
var ExploreCuratedCollectionsBase = function() {
    var strictLocalize = useStrictLocalization().strictLocalize;
    var isSSR = useIsSSR();
    var viewport = useViewport();
    var linkHeight = useResponsiveValue({
        mobileAndAbove: 338,
        tabletAndAbove: 260,
        desktopAndAbove: 350
    });
    return /*#__PURE__*/ _jsxs(VanillaBackground, {
        children: [
            /*#__PURE__*/ _jsxs(StyledContentContainer, {
                children: [
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: viewport.isMobile ? "4x" : "7x"
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        align: "center",
                        variant: viewport.isMobile ? "subheadingSansRegular" : "sectionHeaderSansMedium",
                        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "6DxDti",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "More curated collections to explore"
                                }
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: viewport.isMobile ? "4x" : "5x"
                    }),
                    /*#__PURE__*/ _jsx(Wrapper, {
                        children: cardValues(strictLocalize).map(function(card, index) {
                            var _optimizeImageUrl;
                            return /*#__PURE__*/ _jsxs(React.Fragment, {
                                children: [
                                    index === 0 ? null : /*#__PURE__*/ _jsx(Spacer, {
                                        width: {
                                            mobileAndAbove: "2x",
                                            desktopAndAbove: "3x"
                                        }
                                    }),
                                    index !== 0 && viewport.isMobile ? /*#__PURE__*/ _jsx(Spacer, {
                                        height: "7x"
                                    }) : null,
                                    /*#__PURE__*/ _jsxs(StyledColumn, {
                                        children: [
                                            /*#__PURE__*/ _jsx(Anchor, {
                                                href: card.linkUrl,
                                                children: /*#__PURE__*/ _jsx(Image, {
                                                    src: (_optimizeImageUrl = optimizeImageUrl({
                                                        url: card.imageUrl
                                                    }, isSSR, {
                                                        height: linkHeight
                                                    })) !== null && _optimizeImageUrl !== void 0 ? _optimizeImageUrl : EAEAEA_PIXEL,
                                                    alt: card.imageAlt,
                                                    height: linkHeight
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(Spacer, {
                                                height: viewport.isMobile ? "2x" : "3x"
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                variant: "pageHeaderSerifRegular",
                                                children: card.title
                                            }),
                                            /*#__PURE__*/ _jsx(Spacer, {
                                                height: "1x"
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                children: card.description
                                            }),
                                            /*#__PURE__*/ _jsx(Spacer, {
                                                height: "3x"
                                            }),
                                            /*#__PURE__*/ _jsx(TextLink, {
                                                linkTo: card.linkUrl,
                                                variant: "tertiary",
                                                children: card.linkText
                                            })
                                        ]
                                    }, index)
                                ]
                            }, index);
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: isLoggedInRetailer() ? "10x" : "7x"
            })
        ]
    });
};
export var ExploreCuratedCollections = observer(ExploreCuratedCollectionsBase);
var StyledContentContainer = styled(ContentContainer).withConfig({
    componentId: "sc-7e59df3c-0"
})([
    "",
    "{padding:0 24px;}"
], responsiveMediaQueries.mobile);
var Wrapper = styled(Flex).attrs({
    direction: {
        mobileAndAbove: "column",
        tabletAndAbove: "row"
    },
    justify: {
        mobileAndAbove: "normal",
        tabletAndAbove: "center"
    }
}).withConfig({
    componentId: "sc-7e59df3c-1"
})([
    "",
    "{flex-wrap:wrap;}"
], responsiveMediaQueries.tabletAndAbove);
var StyledColumn = styled(Column).withConfig({
    componentId: "sc-7e59df3c-2"
})([
    "",
    "{width:230px;}",
    "{width:300px;}"
], responsiveMediaQueries.tablet, responsiveMediaQueries.desktopAndAbove);
var TextLink = styled(Button).withConfig({
    componentId: "sc-7e59df3c-3"
})([
    "justify-content:flex-start;"
]);
var Image = styled.img.withConfig({
    componentId: "sc-7e59df3c-4"
})([
    "display:block;object-fit:cover;width:100%;"
]);
var cardValues = function(strictLocalize) {
    return [
        {
            imageUrl: "https://cdn.faire.com/fastly/c4fda321cefd09dcfa854e6520661ab16dfc921af85506cc3c800c457c76eeaf.jpeg",
            imageAlt: "Fashionable Person holding a Purse",
            title: strictLocalize({
                id: "ERr12A",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Guaranteed Fair Trade"
                    }
                ]
            }),
            description: strictLocalize({
                id: "AExXTz",
                defaultMessage: [
                    {
                        type: 0,
                        value: "A global community of brands verified by the World Fair Trade Organization."
                    }
                ]
            }),
            linkText: strictLocalize({
                id: "yirM+a",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Shop Fair Trade"
                    }
                ]
            }),
            linkUrl: "/category/Guaranteed%20Fair%20Trade"
        },
        {
            imageUrl: "https://cdn.faire.com/fastly/95fc5604b28801c192cf0b4bd7703407bc565fdcb5257bf283fa23315e033167.jpeg",
            imageAlt: "Three Sets of Fancy Earrings",
            title: strictLocalize({
                id: "qvmMfp",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Stocking Stuffers"
                    }
                ]
            }),
            description: strictLocalize({
                id: "PlDSu1",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Small gifts, big fun."
                    }
                ]
            }),
            linkText: strictLocalize({
                id: "0sFtf2",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Shop Holiday Gifts"
                    }
                ]
            }),
            linkUrl: "/category/Stocking%20Stuffers"
        },
        {
            imageUrl: "https://cdn.faire.com/fastly/af4257021803dff6a6e90df40d7c63afb854c099ed42d5009f28e3d46cc74e38.jpeg",
            imageAlt: "Collection of Vases",
            title: strictLocalize({
                id: "b6zjkH",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Based in France"
                    }
                ]
            }),
            description: strictLocalize({
                id: "eZ4MSW",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Get inspired by hundreds of independent French brands."
                    }
                ]
            }),
            linkText: strictLocalize({
                id: "7X921q",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Shop French Brands"
                    }
                ]
            }),
            linkUrl: "/category/Based%20in%20France"
        }
    ];
};
